import React, { useState, useEffect, useRef } from 'react';
import { API_URL } from '../config';

export default function ActivityTemplatesModal({ isDarkMode, availableStatuses, onClose, userId }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = new, string = editing id
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const dragIndex = useRef(null);

  function emptyForm() {
    return { name: '', status: '', description: '', daysFromNow: '', timeOfDay: '17:00' };
  }

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/activity-templates?userId=${userId || ''}`);
      if (res.ok) setTemplates(await res.json());
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setForm(emptyForm());
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (t) => {
    setForm({
      name: t.name || '',
      status: t.status || '',
      description: t.description || '',
      daysFromNow: t.daysFromNow != null ? String(t.daysFromNow) : '',
      timeOfDay: t.timeOfDay || '17:00',
    });
    setEditingId(t._id);
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm());
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        status: form.status,
        description: form.description,
        daysFromNow: form.daysFromNow !== '' ? Number(form.daysFromNow) : null,
        timeOfDay: form.timeOfDay || '17:00',
        userId: userId || undefined,
      };
      let res;
      if (editingId) {
        res = await fetch(`${API_URL}/activity-templates/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      } else {
        res = await fetch(`${API_URL}/activity-templates`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      }
      if (res.ok) {
        cancelForm();
        await fetchTemplates();
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    await fetch(`${API_URL}/activity-templates/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: userId || undefined }),
    });
    setDeleteConfirm(null);
    await fetchTemplates();
  };

  const handleReorder = async (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    const newTemplates = [...templates];
    const [moved] = newTemplates.splice(fromIndex, 1);
    newTemplates.splice(toIndex, 0, moved);
    setTemplates(newTemplates);
    await Promise.all(
      newTemplates.map((t, i) =>
        fetch(`${API_URL}/activity-templates/${t._id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: t.name,
            status: t.status,
            description: t.description,
            daysFromNow: t.daysFromNow,
            timeOfDay: t.timeOfDay || '17:00',
            order: i,
            userId: userId || undefined,
          }),
        })
      )
    );
  };

  const bg = isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900';
  const border = isDarkMode ? 'border-gray-700' : 'border-gray-200';
  const inputCls = `w-full px-3 py-2 border rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`;
  const subText = isDarkMode ? 'text-gray-400' : 'text-gray-500';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4" onClick={onClose}>
      <div
        className={`rounded-xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col ${bg}`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-center justify-between px-5 py-4 border-b ${border} shrink-0`}>
          <h2 className="font-bold text-base">⚡ Šablony aktivit</h2>
          <button onClick={onClose} className={`text-xl leading-none px-2 py-1 rounded transition ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>×</button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {/* Form */}
          {showForm && (
            <div className={`rounded-lg border p-4 mb-5 ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-blue-50 border-blue-100'}`}>
              <p className="font-semibold text-sm mb-3">{editingId ? 'Upravit šablonu' : 'Nová šablona'}</p>
              <div className="space-y-3">
                <div>
                  <label className={`block text-xs font-medium mb-1 ${subText}`}>Název šablony *</label>
                  <input
                    className={inputCls}
                    placeholder="Např. Naplánovat schůzku"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  />
                </div>
                <div>
                  <label className={`block text-xs font-medium mb-1 ${subText}`}>Stav (předvyplnit)</label>
                  <select
                    className={inputCls}
                    value={form.status}
                    onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                  >
                    <option value="">— nezadávat —</option>
                    {(availableStatuses || []).map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${subText}`}>Naplánovat za (dní)</label>
                    <input
                      className={inputCls}
                      type="number"
                      min="0"
                      placeholder="Např. 10"
                      value={form.daysFromNow}
                      onChange={e => setForm(f => ({ ...f, daysFromNow: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${subText}`}>Čas</label>
                    <input
                      className={inputCls}
                      type="time"
                      value={form.timeOfDay}
                      onChange={e => setForm(f => ({ ...f, timeOfDay: e.target.value }))}
                    />
                  </div>
                </div>
                <div>
                  <label className={`block text-xs font-medium mb-1 ${subText}`}>Popis aktivity (předvyplnit)</label>
                  <textarea
                    className={`${inputCls} resize-none`}
                    rows={3}
                    placeholder="Text, který se předvyplní do popisu…"
                    value={form.description}
                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button
                  onClick={handleSave}
                  disabled={!form.name.trim() || saving}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg text-sm font-semibold transition"
                >
                  {saving ? 'Ukládám…' : 'Uložit'}
                </button>
                <button
                  onClick={cancelForm}
                  className={`px-4 py-2 rounded-lg text-sm transition border ${isDarkMode ? 'border-gray-600 hover:bg-gray-600' : 'border-gray-300 hover:bg-gray-100'}`}
                >
                  Zrušit
                </button>
              </div>
            </div>
          )}

          {/* Template list */}
          {loading ? (
            <div className={`text-center py-6 ${subText}`}>Načítám…</div>
          ) : templates.length === 0 && !showForm ? (
            <div className={`text-center py-6 ${subText}`}>Žádné šablony. Přidejte první.</div>
          ) : (
            <div className="space-y-2">
              {templates.map((t, index) => (
                <div
                  key={t._id}
                  draggable
                  onDragStart={() => { dragIndex.current = index; }}
                  onDragOver={e => e.preventDefault()}
                  onDrop={() => handleReorder(dragIndex.current, index)}
                  className={`rounded-lg border px-4 py-3 flex items-start justify-between gap-3 ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}
                >
                  <span
                    className={`mt-0.5 mr-1 cursor-grab shrink-0 text-base select-none ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}
                    title="Přetáhněte pro změnu pořadí"
                  >☰</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">{t.name}</p>
                    <div className={`flex flex-wrap gap-2 mt-1 text-xs ${subText}`}>
                      {t.status && <span className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 px-2 py-0.5 rounded-full">{t.status}</span>}
                      {t.daysFromNow != null && <span>📅 +{t.daysFromNow} dní {t.timeOfDay}</span>}
                      {t.description && <span className="truncate max-w-[200px]">📝 {t.description.slice(0, 60)}{t.description.length > 60 ? '…' : ''}</span>}
                    </div>
                  </div>
                  {deleteConfirm === t._id ? (
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-xs ${subText}`}>Smazat?</span>
                      <button onClick={() => handleDelete(t._id)} className="text-xs px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded">Ano</button>
                      <button onClick={() => setDeleteConfirm(null)} className={`text-xs px-2 py-1 border rounded ${isDarkMode ? 'border-gray-500 hover:bg-gray-600' : 'border-gray-300 hover:bg-gray-100'}`}>Ne</button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => openEdit(t)} className={`p-1.5 rounded transition text-sm ${isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-100'}`} title="Upravit">✏️</button>
                      <button onClick={() => setDeleteConfirm(t._id)} className={`p-1.5 rounded transition text-sm ${isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-100'}`} title="Smazat">🗑️</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`px-5 py-3 border-t ${border} shrink-0`}>
          <button
            onClick={openNew}
            disabled={showForm}
            className="px-4 py-2 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-400 text-white rounded-lg text-sm font-semibold transition"
          >
            + Přidat šablonu
          </button>
        </div>
      </div>
    </div>
  );
}
